import { Component } from 'react';
import '../../../../styles/components/auth.scss';
import { ArrowLeft, Loader2, Lock, Mail, MoveRight, UserRound } from 'lucide-react';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import { withRouter } from '../../../../hoc/withRouter';
import { registerUser } from './services/authService';
import { toast } from 'react-toastify';
import i18n from '../../../../i18n';

class RegisterNew extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fullName: null,
      username: null,
      email: null,
      phoneNumber: null,
      password: null,
      showPassword: false,
      confirmPassword: null,
      showConfirmPassword: false,
      agreeToTerms: false,
      isSubmitting: false,
      errors: {},
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({ [name]: type === 'checkbox' ? checked : value });
  };

  handlePhoneChange = (value) => {
    this.setState({ phoneNumber: value });
  };

  toggleVisibility = (field) => {
    this.setState((prevState) => ({
      [field]: !prevState[field],
    }));
  };

  validateForm = () => {
    const errors = {};
    const { fullName, email, username, phoneNumber, password, confirmPassword, agreeToTerms } =
      this.state;

    if (!fullName) errors.fullName = 'Full Name is required';
    if (!username) errors.username = 'Username is required';
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Invalid email format';
    }
    if (!phoneNumber) errors.phoneNumber = 'Phone Number is required';
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      errors.confirmPassword = 'Confirm Password is required';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!agreeToTerms) errors.agreeToTerms = 'You must agree to the terms and conditions';

    this.setState({ errors });

    return Object.keys(errors).length === 0;
  };

  renderFieldError = (fieldName) => {
    if (this.state.errors[fieldName]) {
      return <div className="field-error">{this.state.errors[fieldName]}</div>;
    }
    return null;
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    if (!this.validateForm()) {
      return;
    }
    const { fullName, email, username, phoneNumber, password, confirmPassword } = this.state;
    const phone = parsePhoneNumber(phoneNumber);
    const countryCodeWithPlus = phone ? `+${phone.countryCallingCode}` : '';

    this.setState({ isSubmitting: true });
    try {
      const result = await registerUser({
        name: fullName,
        username: username,
        email: email,
        country_code: countryCodeWithPlus,
        mobile: phone?.nationalNumber,
        password: password,
        password_confirmation: confirmPassword,
      });

      const { response } = result;
      // Check if verification is required (OTP/email)
      if (response?.data?.requires_verification) {
        this.props.navigate(`/login/otp-verify-email/${response.data.verification_token}`, {
          state: {
            data: response.data.email,
            type: 'email',
          },
        });
        return;
      }
    } catch (error) {
      // ── Map API field errors into state ──────────────────────────
      const apiErrors = error?.response?.data?.errors;

      if (apiErrors && typeof apiErrors === 'object') {
        const fieldMap = {
          username: 'username',
          email: 'email',
          mobile: 'phoneNumber', // API uses "mobile", form uses "phoneNumber"
        };

        const mappedErrors = {};

        Object.entries(apiErrors).forEach(([apiField, messages]) => {
          const stateField = fieldMap[apiField] ?? apiField;
          // messages is an array — take the first one
          mappedErrors[stateField] = Array.isArray(messages) ? messages[0] : messages;
        });
        console.log(mappedErrors, 'l');

        this.setState({ errors: mappedErrors });
      } else {
        // Fallback for network errors or unexpected shape
        toast.error(error.message || 'Register failed. Please try again.');
      }
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleLogin = () => {
    this.props.navigate(`/login`);
  };

  render() {
    const {
      fullName,
      email,
      username,
      phoneNumber,
      password,
      showPassword,
      confirmPassword,
      showConfirmPassword,
      agreeToTerms,
      errors,
      isSubmitting,
    } = this.state;

    return (
      <div className="register-container">
        <div className="login-left-section">
          <div className="auth-content">
            <div className="accent-line"></div>
            <h1 className="title"> {i18n.t('register_banner_title')}</h1>
            <p className="subtitle-l2">{i18n.t('login_banner_subtitle')}</p>
          </div>
        </div>

        <div className="login-right-section">
          <div className="login-card">
            <h3 className="welcome-title">{i18n.t('create_account')}</h3>
            <p className="welcome-subtitle">{i18n.t('register_card_subtitle')}</p>

            <form onSubmit={this.handleSubmit}>
              {/* Full Name */}
              <div className="form-group-l2">
                <label htmlFor="fullName">{i18n.t('full_name')}</label>
                <div className="input-wrapper">
                  <UserRound strokeWidth={1.5} className="input-icon-l2" />
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={fullName}
                    onChange={this.handleInputChange}
                    placeholder={i18n.t('validate_name')}
                    className={`form-input-l2 ${errors?.fullName ? 'error' : ''}`}
                  />
                </div>
                {this.renderFieldError('fullName')}
              </div>
              {/*  Username */}
              <div className="form-group-l2">
                <label htmlFor="username">{i18n.t('username')}</label>
                <div className="input-wrapper">
                  <UserRound strokeWidth={1.5} className="input-icon-l2" />
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={username}
                    onChange={this.handleInputChange}
                    placeholder={i18n.t('validate_username')}
                    className={`form-input-l2 ${errors?.username ? 'error' : ''}`}
                  />
                </div>
                {this.renderFieldError('username')}
              </div>
              {/* Email */}
              <div className="form-group-l2">
                <label htmlFor="email">{i18n.t('email_address')}</label>
                <div className="input-wrapper">
                  <Mail strokeWidth={1.5} className="input-icon-l2" />
                  <input
                    type="text"
                    id="email"
                    name="email"
                    value={email}
                    onChange={this.handleInputChange}
                    placeholder={i18n.t('validate_email')}
                    className={`form-input-l2 ${errors?.email ? 'error' : ''}`}
                  />
                </div>
                {this.renderFieldError('email')}
              </div>

              {/*  Mobile Number */}
              <div className="form-group-l2 left-right">
                <label htmlFor="phoneNumber">{i18n.t('mobile_number')}</label>
                <div className="input-wrapper">
                  <PhoneInput
                    international
                    defaultCountry="OM"
                    value={phoneNumber}
                    onChange={this.handlePhoneChange}
                    placeholder="Enter phone number"
                    className={`custom-phone-input ${errors?.phoneNumber ? 'error' : ''}`}
                    dir="ltr"
                  />
                </div>
                {this.renderFieldError('phoneNumber')}
              </div>
              {/* Password  */}
              <div className="form-group-l2">
                <label htmlFor="password">{i18n.t('password')}</label>
                <div className="input-wrapper">
                  <Lock strokeWidth={1.5} className="input-icon-l2" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={password}
                    onChange={this.handleInputChange}
                    placeholder="••••••••"
                    className={`form-input-l2 ${errors?.password ? 'error' : ''}`}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => this.toggleVisibility('showPassword')}
                    aria-label="Toggle password visibility"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {showPassword ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {this.renderFieldError('password')}
              </div>
              {/* Confirm Password */}
              <div className="form-group-l2">
                <label htmlFor="confirmPassword">{i18n.t('confirm_password')}</label>
                <div className="input-wrapper">
                  <Lock strokeWidth={1.5} className="input-icon-l2" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={this.handleInputChange}
                    placeholder="••••••••"
                    className={`form-input-l2 ${errors?.confirmPassword ? 'error' : ''}`}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => this.toggleVisibility('showConfirmPassword')}
                    aria-label="Toggle password visibility"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {showConfirmPassword ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {this.renderFieldError('confirmPassword')}
              </div>
              {/* Terms and Conditions */}
              <div className="checkbox-group-l2">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={this.handleInputChange}
                  className="checkbox-input-l2"
                />
                <label htmlFor="agreeToTerms" className="checkbox-label-l2">
                  {i18n.t('i_agree_to_the')}{' '}
                  <a href="/terms-of-service" className="link-inline">
                    {i18n.t('terms_of_service')}{' '}
                  </a>
                  {i18n.t('and')}{' '}
                  <a href="/privacy-policy" className="link-inline">
                    {i18n.t('privacy_policy')}
                  </a>
                </label>
              </div>

              <div className="register-buttons">
                <button type="submit" disabled={isSubmitting} className="button1 button1-gradient ">
                  {i18n.t('register_account')}
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <MoveRight strokeWidth={1.5} />
                  )}
                </button>
              </div>

              <div className="register-section">
                <p className="register-text">{i18n.t('dont_have_an_account')}</p>
                <div className="register-buttons">
                  <button
                    type="button"
                    onClick={this.handleLogin}
                    className="button1 button1-outline"
                  >
                    <ArrowLeft strokeWidth={1.5} />
                    {i18n.t('back_to_login')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(RegisterNew);
